// Quote-page pieces for Time & Materials quotes (slice 1d). Every component renders NOTHING for a
// fixed quote unless the page offers the toggle, so a fixed quote's page is unchanged.
import React from 'react';
import { Link } from 'react-router-dom';
import { fmtMoney } from '../../utils/priceMath';
import { TM_QUOTE_HELP, tmLineEstimateUnit } from '../../utils/tmQuote';

/** Small "T&M" badge for list rows and the header. */
export function TmBadge({ className = '', light = false, title = 'Time & Materials: priced at completion' }) {
  return (
    <span className={`badge ${light ? 'bg-light text-dark' : 'bg-info text-dark'} ${className}`} style={{ fontSize: '0.65rem' }} title={title}>
      T&amp;M
    </span>
  );
}

/**
 * "Pricing: Fixed | Time & Materials". Shown only when the flag is on and the customer is allowed,
 * or when the quote already is T&M (so it can be switched back). Editing is governed by the
 * page's Draft-only fieldset.
 */
export function TmQuotePricingToggle({ mode, offer, onChange }) {
  if (!offer && mode !== 'tm') return null;
  const isTmMode = mode === 'tm';
  return (
    <div className="row g-2 mb-3">
      <div className="col-12">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="form-label small text-muted mb-0">Pricing</span>
          <div className="btn-group btn-group-sm" role="group" aria-label="Pricing">
            <button type="button" className={`btn ${!isTmMode ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => onChange('fixed')}>
              Fixed
            </button>
            <button
              type="button"
              className={`btn ${isTmMode ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => onChange('tm')}
              disabled={!offer && !isTmMode}
            >
              Time &amp; Materials
            </button>
          </div>
        </div>
        {isTmMode && (
          <div className="form-text mt-1" style={{ fontSize: '0.78rem' }}>
            <i className="bi bi-info-circle me-1"></i>{TM_QUOTE_HELP}
          </div>
        )}
      </div>
    </div>
  );
}

/** Read-only price cell of a T&M line: "T&M · est. $X". */
export function TmQuoteLinePrice({ line }) {
  const est = tmLineEstimateUnit(line);
  return (
    <div
      className="form-control form-control-sm bg-light text-muted"
      title="Time & Materials: this line has no price until the order is priced on the Sales Order. The estimate is the calculator's price at quote time (an internal note)."
    >
      T&amp;M · est. {est == null ? '—' : fmtMoney(est)}
    </div>
  );
}

/** Read-only subtotal cell of a T&M line: "T&M · est. $X" over the quantity. */
export function TmQuoteLineSubtotal({ line }) {
  const est = tmLineEstimateUnit(line);
  const qty = parseInt(line && line.quantity, 10) || 0;
  return (
    <div className="form-control form-control-sm bg-light text-muted" title="Estimated unit × quantity (internal note, not billed)">
      T&amp;M · est. {est == null ? '—' : fmtMoney(est * qty)}
    </div>
  );
}

/**
 * Totals block of a T&M quote: "Priced at completion", tax 0, and the two optional internal
 * budget notes (estimate, not-to-exceed). Shipping / fees / discount stay editable: the Sales
 * Order carries them to the close-out invoice.
 */
export function TmQuoteTotals({
  shipping, fees, discount, onShipping, onFees, onDiscount,
  estimate, nte, onEstimate, onNte, defaultEstimate,
}) {
  return (
    <div className="d-flex align-items-end gap-2 p-2 border rounded bg-light flex-wrap" style={{ fontSize: '0.85rem' }}>
      <div style={{ width: '150px' }}>
        <label className="form-label small mb-0">Subtotal</label>
        <div className="form-control form-control-sm text-muted" title="A T&M order is priced on the Sales Order after it ships.">Priced at completion</div>
      </div>
      <div style={{ width: '90px' }}>
        <label className="form-label small mb-0">Shipping</label>
        <input type="number" step="0.01" className="form-control form-control-sm" value={shipping} onChange={(e) => onShipping(parseFloat(e.target.value) || 0)} />
      </div>
      <div style={{ width: '90px' }}>
        <label className="form-label small mb-0">Fees</label>
        <input type="number" step="0.01" className="form-control form-control-sm" value={fees} onChange={(e) => onFees(parseFloat(e.target.value) || 0)} />
      </div>
      <div style={{ width: '90px' }}>
        <label className="form-label small mb-0">Discount</label>
        <input type="number" step="0.01" className="form-control form-control-sm" value={discount} onChange={(e) => onDiscount(parseFloat(e.target.value) || 0)} />
      </div>
      <div style={{ width: '80px' }}>
        <label className="form-label small mb-0">Tax</label>
        <div className="form-control form-control-sm text-muted" title="Tax is worked out when the order is priced.">0.00</div>
      </div>
      <div style={{ width: '220px' }}>
        <label className="form-label small mb-0" htmlFor="tm-estimate">Internal budget note: estimate (optional)</label>
        <input
          id="tm-estimate"
          type="text"
          inputMode="decimal"
          className="form-control form-control-sm"
          value={estimate}
          placeholder={defaultEstimate != null ? `default ${fmtMoney(defaultEstimate)}` : 'optional'}
          onChange={(e) => onEstimate(e.target.value)}
          title="Not shown to the customer. Left blank, the estimate is the calculator's price × quantity of the lines."
        />
      </div>
      <div style={{ width: '200px' }}>
        <label className="form-label small mb-0" htmlFor="tm-nte">Internal budget note: not-to-exceed (optional)</label>
        <input
          id="tm-nte"
          type="text"
          inputMode="decimal"
          className="form-control form-control-sm"
          value={nte}
          placeholder="optional"
          onChange={(e) => onNte(e.target.value)}
          title="Not shown to the customer. Pricing above it needs a reason at Approve T&M Pricing."
        />
      </div>
    </div>
  );
}

/**
 * Banner on an Ordered T&M quote: where the order is priced ("Price on SO-x" deep-links to the
 * Sales Order with the pricing modal open while pricing is pending).
 */
export function TmQuoteOrderedBanner({ quote }) {
  if (!quote || quote.pricing_mode !== 'tm' || !quote.generated_sales_order_id) return null;
  const soLabel = quote.generated_sales_order_number || `SO #${quote.generated_sales_order_id}`;
  const final = quote.tm_pricing_status === 'final';
  return (
    <div className={`alert ${final ? 'alert-success' : 'alert-info'} d-flex align-items-center gap-2 py-2 mb-3`}>
      <i className="bi bi-calculator"></i>
      <div className="flex-grow-1">
        {final
          ? <>This T&amp;M quote&apos;s order {soLabel} has been priced.</>
          : <>This T&amp;M quote became {soLabel}. The order is priced there after it ships.</>}
      </div>
      <Link
        className={`btn btn-sm ${final ? 'btn-outline-success' : 'btn-primary'}`}
        to={`/sales-orders?id=${quote.generated_sales_order_id}${final ? '' : '&tm=price'}`}
      >
        {final ? `Open ${soLabel}` : `Price on ${soLabel}`}
      </Link>
    </div>
  );
}
